/**
 * Billing Store
 * Tech-Spec: Stripe Self-Serve Billing — Task 21
 *
 * Pinia store for billing status, subscription management, and invoice history.
 * Only fetches for admins.
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useRoles } from "@/composables/useRoles";
import { useApi } from "@/lib/api";

export interface BillingStatus {
  status: string;
  trialEndsAt: string | null;
  trialDaysRemaining: number | null;
  isTrialExpired: boolean;
  isSoftLocked: boolean;
  seatCount: number;
  estimatedMonthlyCost: number;
  currentPeriodEnd: string | null;
  isGrandfathered: boolean;
  /** True when a Pando connection waives billing — Aspen is free for them. */
  billingWaived?: boolean;
}

export interface Invoice {
  id: string;
  date: string | null;
  amount: number;
  currency: string;
  status: string;
  pdfUrl: string | null;
}

export interface PaymentMethodInfo {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

const CACHE_KEY = "pando:billing:status:v2";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

export const useBillingStore = defineStore("billing", () => {
  const { apiFetch } = useApi();
  const roles = useRoles();

  // State
  const subscription = ref<BillingStatus | null>(null);
  const invoices = ref<Invoice[]>([]);
  const paymentMethod = ref<PaymentMethodInfo | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Computed
  const status = computed(() => subscription.value?.status ?? null);
  const trialDaysRemaining = computed(
    () => subscription.value?.trialDaysRemaining ?? null,
  );
  const isTrialExpired = computed(
    () => subscription.value?.isTrialExpired ?? false,
  );
  const isSoftLocked = computed(
    () => subscription.value?.isSoftLocked ?? false,
  );
  const isTrialWarning = computed(() => {
    const days = trialDaysRemaining.value;
    return days !== null && days <= 7 && days > 0;
  });
  const seatCount = computed(() => subscription.value?.seatCount ?? 0);
  const estimatedMonthlyCost = computed(
    () => subscription.value?.estimatedMonthlyCost ?? 0,
  );
  const isGrandfathered = computed(
    () => subscription.value?.isGrandfathered ?? false,
  );
  const billingWaived = computed(
    () => subscription.value?.billingWaived ?? false,
  );

  // Actions
  async function loadBillingStatus(force = false): Promise<void> {
    // Admins get full billing status; non-admins get lightweight soft-lock state
    if (roles.isAdmin.value) {
      await loadFullBillingStatus(force);
    } else {
      await loadLightweightState();
    }
  }

  async function loadFullBillingStatus(force: boolean): Promise<void> {
    // Check sessionStorage cache
    if (!force) {
      const cached = getCachedStatus();
      if (cached) {
        subscription.value = cached;
        return;
      }
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await apiFetch("/api/billing/status");
      if (response.ok) {
        const data = await response.json();
        subscription.value = data;
        cacheStatus(data);
      } else {
        error.value = "Failed to load billing status";
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Unknown error";
    } finally {
      isLoading.value = false;
    }
  }

  async function loadLightweightState(): Promise<void> {
    try {
      const response = await apiFetch("/api/billing/state", {
        suppressErrorToast: true,
      });
      if (response.ok) {
        const data = await response.json();
        // Create a minimal subscription object for the banner to read
        if (data.isSoftLocked) {
          subscription.value = {
            status: "trial_expired",
            trialEndsAt: null,
            trialDaysRemaining: null,
            isTrialExpired: true,
            isSoftLocked: true,
            seatCount: 0,
            estimatedMonthlyCost: 0,
            currentPeriodEnd: null,
            isGrandfathered: false,
          };
        }
      }
    } catch {
      // Silent fail for non-admin state check
    }
  }

  async function createSetupIntent(): Promise<string | null> {
    try {
      const response = await apiFetch("/api/billing/setup-intent", {
        method: "POST",
      });
      if (response.ok) {
        const data = await response.json();
        return data.clientSecret;
      }
      return null;
    } catch {
      return null;
    }
  }

  async function activateSubscription(
    paymentMethodId: string,
  ): Promise<boolean> {
    try {
      const response = await apiFetch("/api/billing/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethodId, consentGiven: true }),
      });

      if (response.ok) {
        // Refresh billing status after activation
        await loadBillingStatus(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async function loadPaymentMethod(): Promise<void> {
    try {
      const response = await apiFetch("/api/billing/payment-method");
      if (response.ok) {
        const data = await response.json();
        paymentMethod.value = data.paymentMethod ?? null;
      }
    } catch {
      // Silent fail
    }
  }

  async function updatePaymentMethod(
    paymentMethodId: string,
  ): Promise<boolean> {
    try {
      const response = await apiFetch("/api/billing/update-payment-method", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethodId }),
      });
      if (response.ok) {
        await loadPaymentMethod();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async function loadInvoices(): Promise<void> {
    try {
      const response = await apiFetch("/api/billing/invoices");
      if (response.ok) {
        const data = await response.json();
        invoices.value = data.invoices ?? [];
      }
    } catch {
      // Silent fail for invoice loading
    }
  }

  // Cache helpers
  function getCachedStatus(): BillingStatus | null {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const { data, timestamp } = JSON.parse(raw);
      if (Date.now() - timestamp > CACHE_TTL_MS) {
        sessionStorage.removeItem(CACHE_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  function cacheStatus(data: BillingStatus): void {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data, timestamp: Date.now() }),
      );
    } catch {
      // sessionStorage may be full or unavailable
    }
  }

  return {
    // State
    subscription,
    invoices,
    paymentMethod,
    isLoading,
    error,
    // Computed
    status,
    trialDaysRemaining,
    isTrialExpired,
    isSoftLocked,
    isTrialWarning,
    seatCount,
    estimatedMonthlyCost,
    isGrandfathered,
    billingWaived,
    // Actions
    loadBillingStatus,
    createSetupIntent,
    activateSubscription,
    loadInvoices,
    loadPaymentMethod,
    updatePaymentMethod,
  };
});
